import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Mail, RefreshCw, UserMinus, Users } from "lucide-react";
import { useAuth } from "./AuthContext";
import {
  createInvite,
  fetchInvites,
  fetchMembers,
  removeMember,
  resendInvite,
  revokeInvite,
  updateMemberRole,
} from "./api";
import {
  canChangeMemberRole,
  canInviteRole,
  canManageWorkspace,
  canRemoveMember,
  canViewMembers,
  roleDisplayName,
} from "./orgPermissions";
import "./WorkspaceTeamPanel.css";

const INVITE_ROLES = [
  { value: "viewer", label: "Viewer" },
  { value: "member", label: "Engineer" },
  { value: "admin", label: "Admin" },
];

const MEMBER_ROLES = [
  { value: "viewer", label: "Viewer" },
  { value: "member", label: "Engineer" },
  { value: "admin", label: "Admin" },
  { value: "owner", label: "Owner" },
];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return "—";
  }
}

function getErrorMessage(err) {
  return err?.message || err?.error || "Something went wrong. Please try again.";
}

export default function WorkspaceTeamPanel({ organizationId, workspaceName }) {
  const { user } = useAuth();
  const permissionUser = useMemo(
    () => ({
      ...user,
      organizationRole: user?.organizationRole || "viewer",
    }),
    [user]
  );

  const canView = canViewMembers(permissionUser);
  const canManage = canManageWorkspace(permissionUser);

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  const availableInviteRoles = INVITE_ROLES.filter((option) =>
    canInviteRole(permissionUser, option.value)
  );

  const loadTeamData = useCallback(async () => {
    if (!organizationId || !canView) {
      setMembers([]);
      setInvites([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [membersResult, invitesResult] = await Promise.all([
        fetchMembers(organizationId),
        fetchInvites(organizationId),
      ]);
      setMembers(membersResult.members || []);
      setInvites(invitesResult.invites || []);
    } catch (err) {
      setError(getErrorMessage(err));
      setMembers([]);
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, canView]);

  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  const handleCreateInvite = async (event) => {
    event.preventDefault();
    if (!canManage || !organizationId) return;

    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      setError("Email is required.");
      return;
    }
    if (!canInviteRole(permissionUser, inviteRole)) {
      setError("You cannot invite users with that role.");
      return;
    }

    setActionLoading("invite");
    setError("");
    setSuccess("");
    try {
      await createInvite(organizationId, { email, role: inviteRole });
      setInviteEmail("");
      setSuccess(`Invitation sent to ${email}.`);
      await loadTeamData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading("");
    }
  };

  const handleResendInvite = async (inviteId) => {
    setActionLoading(`resend-${inviteId}`);
    setError("");
    setSuccess("");
    try {
      await resendInvite(organizationId, inviteId);
      setSuccess("Invitation resent.");
      await loadTeamData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading("");
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm("Revoke this invitation?")) return;
    setActionLoading(`revoke-${inviteId}`);
    setError("");
    setSuccess("");
    try {
      await revokeInvite(organizationId, inviteId);
      setSuccess("Invitation revoked.");
      await loadTeamData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading("");
    }
  };

  const handleRoleChange = async (member, newRole) => {
    if (newRole === member.role) return;
    if (!canChangeMemberRole(permissionUser, member.role, newRole)) {
      setError("You cannot assign that role.");
      return;
    }

    setActionLoading(`role-${member.membershipId}`);
    setError("");
    setSuccess("");
    try {
      await updateMemberRole(organizationId, member.membershipId, newRole);
      setSuccess("Member role updated.");
      await loadTeamData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading("");
    }
  };

  const handleRemoveMember = async (member) => {
    if (
      !canRemoveMember(
        permissionUser,
        member.role,
        user?._id,
        member.userId
      )
    ) {
      return;
    }
    if (!window.confirm(`Remove ${member.email} from this workspace?`)) return;

    setActionLoading(`remove-${member.membershipId}`);
    setError("");
    setSuccess("");
    try {
      await removeMember(organizationId, member.membershipId);
      setSuccess("Member removed.");
      await loadTeamData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading("");
    }
  };

  if (!organizationId) {
    return null;
  }

  if (!canView) {
    return (
      <section className="workspace-team-panel repositories-section">
        <h2>Team</h2>
        <p className="workspace-team-muted">
          Team management is available to workspace admins and owners.
        </p>
      </section>
    );
  }

  const pendingInvites = invites.filter((invite) => invite.status === "pending");

  return (
    <section
      className="workspace-team-panel repositories-section"
      aria-labelledby="workspace-team-heading"
      data-testid="workspace-team-panel"
    >
      <div className="workspace-team-header">
        <div>
          <h2 id="workspace-team-heading">Team</h2>
          {workspaceName ? (
            <p className="workspace-team-subtitle">{workspaceName}</p>
          ) : null}
        </div>
        <button
          type="button"
          className="secondary-button workspace-team-refresh"
          onClick={loadTeamData}
          disabled={loading || Boolean(actionLoading)}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="workspace-team-muted">Loading team…</p>
      ) : (
        <>
          <div className="workspace-team-section">
            <h3>
              <Users size={16} aria-hidden="true" />
              Members ({members.length})
            </h3>
            {members.length === 0 ? (
              <p className="workspace-team-muted">No members found.</p>
            ) : (
              <ul className="workspace-team-list">
                {members.map((member) => {
                  const roleEditable = MEMBER_ROLES.some(
                    (option) =>
                      option.value !== member.role &&
                      canChangeMemberRole(
                        permissionUser,
                        member.role,
                        option.value
                      )
                  );
                  const removable = canRemoveMember(
                    permissionUser,
                    member.role,
                    user?._id,
                    member.userId
                  );

                  return (
                    <li key={member.membershipId} className="workspace-team-row">
                      <div className="workspace-team-row-main">
                        <span className="workspace-team-email">{member.email}</span>
                        <span className="workspace-team-meta">
                          Joined {formatDate(member.joinedAt)}
                        </span>
                      </div>
                      <div className="workspace-team-row-actions">
                        {roleEditable ? (
                          <select
                            className="workspace-team-select"
                            value={member.role}
                            disabled={actionLoading === `role-${member.membershipId}`}
                            onChange={(event) =>
                              handleRoleChange(member, event.target.value)
                            }
                            aria-label={`Role for ${member.email}`}
                          >
                            {MEMBER_ROLES.filter((option) =>
                              option.value === member.role ||
                              canChangeMemberRole(
                                permissionUser,
                                member.role,
                                option.value
                              )
                            ).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="workspace-team-role-badge">
                            {roleDisplayName(member.role)}
                          </span>
                        )}
                        {removable ? (
                          <button
                            type="button"
                            className="icon-btn delete workspace-team-remove"
                            onClick={() => handleRemoveMember(member)}
                            disabled={actionLoading === `remove-${member.membershipId}`}
                            title="Remove member"
                            aria-label={`Remove ${member.email}`}
                          >
                            <UserMinus size={16} />
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="workspace-team-section">
            <h3>
              <Mail size={16} aria-hidden="true" />
              Pending invites ({pendingInvites.length})
            </h3>
            {pendingInvites.length === 0 ? (
              <p className="workspace-team-muted">No pending invitations.</p>
            ) : (
              <ul className="workspace-team-list">
                {pendingInvites.map((invite) => (
                  <li key={invite.inviteId} className="workspace-team-row">
                    <div className="workspace-team-row-main">
                      <span className="workspace-team-email">{invite.email}</span>
                      <span className="workspace-team-meta">
                        {roleDisplayName(invite.role)} · expires {formatDate(invite.expiresAt)}
                      </span>
                    </div>
                    {canManage ? (
                      <div className="workspace-team-row-actions">
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => handleResendInvite(invite.inviteId)}
                          disabled={actionLoading === `resend-${invite.inviteId}`}
                        >
                          Resend
                        </button>
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => handleRevokeInvite(invite.inviteId)}
                          disabled={actionLoading === `revoke-${invite.inviteId}`}
                        >
                          Revoke
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {canManage && availableInviteRoles.length > 0 ? (
            <form className="workspace-team-invite-form" onSubmit={handleCreateInvite}>
              <h3>Invite teammate</h3>
              <div className="workspace-team-invite-fields">
                <input
                  type="email"
                  className="form-input"
                  placeholder="email@company.com"
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  aria-label="Invite email"
                  required
                />
                <select
                  className="form-input workspace-team-select"
                  value={inviteRole}
                  onChange={(event) => setInviteRole(event.target.value)}
                  aria-label="Invite role"
                >
                  {availableInviteRoles.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={actionLoading === "invite"}
                >
                  {actionLoading === "invite" ? "Sending…" : "Send invite"}
                </button>
              </div>
            </form>
          ) : null}
        </>
      )}

      {error ? (
        <div className="error-message">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      ) : null}
      {success ? <p className="workspace-team-success">{success}</p> : null}
    </section>
  );
}
